import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from './contexts.tsx';
import { usePermissionsContext } from './usePermissionContext.tsx';

/**
 * Evaluate a `resource:action` against a set of permission strings, honoring
 * `resource:*` and `*:*` wildcards.
 */
const matchesPermission = (
  permissions: readonly string[],
  resource: string,
  action: string,
): boolean => {
  if (permissions.includes(`${resource}:${action}`)) return true;
  if (permissions.includes(`${resource}:*`)) return true;
  if (permissions.includes('*:*')) return true;
  return false;
};

/**
 * User profile information derived from JWT claims
 */
export interface UserProfile {
  sub: string; // User ID
  email: string;
  name: string;
  roles: string[];
  permissions: string[];
  tenantId: string;
  iat: number;
  exp: number;
  /**
   * The principal's preferred BCP-47 locale (OIDC standard `locale` claim), or
   * `undefined` when the token carries none. The shell's locale bootstrap reads
   * this as the highest-priority candidate (user > tenant default > browser),
   * clamped to the locales that actually ship a bundle before it is applied.
   */
  locale?: string;
}

/**
 * Permission check result
 */
export interface PermissionCheckResult {
  allowed: boolean;
  loading: boolean;
  error?: Error;
  /**
   * True only when the entitlement source produced an actual VERDICT.
   *
   * `allowed: false` alone is ambiguous — it is the value both for "the backend
   * says no" and for "we never found out". Conflating them is how an
   * administrator saw *"Your role does not have access to this view"* on the
   * demo while every network call, including both
   * `POST /auth/entitlements/evaluate`, returned 200 and no 403 appeared
   * anywhere. A view must never render a denial the backend did not state, so
   * gate denial on THIS flag, not on `!allowed`.
   */
  resolved: boolean;
}

/**
 * Dev-only escape hatch that mirrors the gateway's `dev-token-*` bypass in
 * `src/Tyndo.Gateway/Middleware/AuthorizationMiddleware.cs`. Playwright auth
 * setup writes a `dev-token-${timestamp}` value into storageState when no
 * Keycloak instance is available; without this synthetic profile every
 * `usePermission` check would return `{ allowed: false }` and gate every UI
 * action behind a forbidden state. Real JWTs continue through `decodeJWT`.
 */
const tryDecodeDevToken = (token: string): UserProfile | null => {
  if (!token.startsWith('dev-token-')) {
    return null;
  }
  const nowSec = Math.floor(Date.now() / 1000);
  return {
    sub: 'dev-user',
    email: 'dev@tyndo.local',
    name: 'Dev User',
    roles: ['*'],
    permissions: ['*:*'],
    tenantId: '00000000-0000-0000-0000-000000000001',
    iat: nowSec,
    exp: nowSec + 24 * 60 * 60,
  };
};

/**
 * Decodes JWT and extracts user profile information
 * Assumes JWT follows standard claims structure
 */
const decodeJWT = (token: string): UserProfile | null => {
  try {
    const parts = token.split('.');
    const payload = parts[1];
    if (parts.length !== 3 || !payload) {
      throw new Error('Invalid JWT format');
    }

    // Decode the payload
    const decoded = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    );

    // Roles may arrive as a plural `roles` array (OIDC/Keycloak) or as the
    // single `role` claim the native-auth gateway emits. Normalise both into a
    // string[] so role gating (admin nav, edit affordances) works regardless of
    // the issuer. Without this, a native-auth principal whose only claim is
    // `role: "Admin"` would read as having no roles at all.
    const rawRoles = decoded.roles ?? decoded.role ?? [];
    const roles = Array.isArray(rawRoles)
      ? rawRoles
      : [rawRoles].filter((r): r is string => typeof r === 'string' && r.length > 0);

    return {
      sub: decoded.sub,
      email: decoded.email,
      name: decoded.name,
      roles,
      permissions: decoded.permissions || [],
      tenantId: decoded.tenantId || decoded.tenant_id,
      iat: decoded.iat,
      exp: decoded.exp,
      // OIDC standard `locale` claim (Keycloak emits it too). Undefined when
      // the issuer carries none — the shell then falls back to browser locale.
      locale: typeof decoded.locale === 'string' ? decoded.locale : undefined,
    };
  } catch (err) {
    console.error('Failed to decode JWT:', err);
    return null;
  }
};

/**
 * Hook to get the current user profile from JWT token
 *
 * @returns User profile or null if not authenticated
 *
 * @example
 * const user = useCurrentUser();
 * if (user) {
 *   console.log(`Hello, ${user.name}`);
 * }
 */
export const useCurrentUser = (): UserProfile | null => {
  const { token } = useAuthContext();
  const { permissions, roles } = usePermissionsContext();
  const [decoded, setDecoded] = useState<UserProfile | null>(null);

  useEffect(() => {
    if (token) {
      setDecoded(tryDecodeDevToken(token) ?? decodeJWT(token));
    } else {
      setDecoded(null);
    }
  }, [token]);

  // Identity (sub/email/name/exp) comes from the JWT, but permissions + roles
  // are authoritative from the gateway-backed PermissionsContext — the gateway
  // resolves them from the DB and does NOT embed a flat `permissions` claim.
  return useMemo<UserProfile | null>(() => {
    if (!decoded) return null;
    return {
      ...decoded,
      permissions: permissions.length > 0 ? permissions : decoded.permissions,
      roles: roles.length > 0 ? roles : decoded.roles,
    };
  }, [decoded, permissions, roles]);
};

/**
 * Hook to check if user has permission for a resource and action
 * Uses both role-based (RBAC) and attribute-based (ABAC) access control
 *
 * @param resource - Resource identifier (e.g., 'equipment', 'production_lines')
 * @param action - Action to perform (e.g., 'read', 'create', 'update', 'delete')
 * @returns Permission check result with allowed flag and loading state
 *
 * @example
 * const { allowed, loading } = usePermission('equipment', 'update');
 * if (allowed && !loading) {
 *   return <EditButton />;
 * }
 */
export const usePermission = (
  resource: string,
  action: string
): PermissionCheckResult => {
  const { permissions, loading, error } = usePermissionsContext();

  return useMemo<PermissionCheckResult>(() => {
    if (loading) return { allowed: false, loading: true, error, resolved: false };
    return {
      allowed: matchesPermission(permissions, resource, action),
      loading: false,
      error,
      // A verdict exists only if the entitlement fetch actually succeeded. When
      // it errored, `permissions` is an empty array for a reason that has
      // nothing to do with this user's entitlements — treating that as "denied"
      // renders a verdict the backend never gave.
      resolved: error === undefined,
    };
  }, [permissions, loading, error, resource, action]);
};

/**
 * Hook to require a specific role
 * Redirects to login if user doesn't have the role
 *
 * @param role - Required role name
 * @param redirectPath - Path to redirect if user doesn't have role (default: '/login')
 * @returns Object with hasRole flag and loading state
 *
 * @example
 * const { hasRole, loading } = useRequireRole('admin');
 * if (!loading && !hasRole) {
 *   return <AccessDenied />;
 * }
 */
export const useRequireRole = (
  role: string,
  redirectPath: string = '/login'
): { hasRole: boolean; loading: boolean } => {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const [hasRole, setHasRole] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      // Not authenticated, redirect to login
      setTimeout(() => {
        navigate(redirectPath);
      }, 0);
      setLoading(false);
      return;
    }

    const userHasRole = user.roles.includes(role);
    setHasRole(userHasRole);
    setLoading(false);

    if (!userHasRole) {
      // User doesn't have required role
      const forbiddenPath = '/forbidden';
      navigate(forbiddenPath);
    }
  }, [user, role, navigate, redirectPath]);

  return { hasRole, loading };
};

/**
 * Hook to check multiple permissions and return overall allowed status
 * Useful for checking if user can perform a complex operation
 *
 * @param checks - Array of [resource, action] tuples to check
 * @param requireAll - If true, all checks must pass; if false, at least one must pass
 * @returns Overall permission result
 *
 * @example
 * const { allowed } = usePermissions(
 *   [['equipment', 'read'], ['production_lines', 'read']],
 *   true // require all
 * );
 */
export const usePermissions = (
  checks: [resource: string, action: string][],
  requireAll: boolean = true
): PermissionCheckResult => {
  const { permissions, loading, error } = usePermissionsContext();

  // No per-element hooks: take the permission set once and evaluate every
  // [resource, action] in a single memo. This keeps the hook count constant
  // even when the `checks` array length changes between renders (the previous
  // Array.map(usePermission) violated the Rules of Hooks and could crash).
  // Serialize checks so the memo only recomputes when the actual tuples change.
  const checksKey = checks.map(([r, a]) => `${r}:${a}`).join('|');

  return useMemo<PermissionCheckResult>(() => {
    if (loading) return { allowed: false, loading: true, error, resolved: false };
    if (checks.length === 0)
      return { allowed: true, loading: false, error, resolved: error === undefined };
    const evaluations = checks.map(([resource, action]) =>
      matchesPermission(permissions, resource, action),
    );
    const allowed = requireAll
      ? evaluations.every(Boolean)
      : evaluations.some(Boolean);
    // Same contract as usePermission: a verdict exists only when the
    // entitlement fetch actually succeeded. See PermissionCheckResult.resolved.
    return { allowed, loading: false, error, resolved: error === undefined };
    // `checks` is intentionally excluded in favor of the stable `checksKey`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions, loading, error, requireAll, checksKey]);
};
